// AutoDistrict — Financing simulation utility (indicative).
// Reference framework:
//   - Loan amount range: €2,500 – €50,000
//   - Term range: 12 – 84 months
//   - APR range: 5.59 % (best profile) → 8.99 % (worst profile)
//   - Second-hand vehicle, purchased from a professional seller, ≤ 5 years old
// All values indicative — final terms subject to credit acceptance
// by our approved financial partner.

const APR_MIN = 0.0559; // best-case APR
const APR_MAX = 0.0899; // worst-case APR
const MAX_VEHICLE_AGE_YEARS = 5;

export const FINANCING = {
  APR_MIN,
  APR_MAX,
  MIN_LOAN: 2500,
  MAX_LOAN: 50000,
  MIN_DURATION: 12,
  MAX_DURATION: 84,
  DEFAULT_DURATION: 60,
  DURATION_STEP: 12,
  LOAN_STEP: 500,
  MAX_VEHICLE_AGE_YEARS,
};

/**
 * Check financing eligibility for a given car.
 * @param {{ price?: number, year?: number }} car
 * @returns {{ eligible: boolean, reason: 'age' | 'price' | null }}
 */
export const isFinanceEligible = ({ price, year } = {}) => {
  const currentYear = new Date().getFullYear();
  if (year && currentYear - Number(year) > MAX_VEHICLE_AGE_YEARS) {
    return { eligible: false, reason: 'age' };
  }
  const p = Number(price) || 0;
  if (p < FINANCING.MIN_LOAN || p > FINANCING.MAX_LOAN) {
    return { eligible: false, reason: 'price' };
  }
  return { eligible: true, reason: null };
};

const clampLoan = (v) => Math.max(FINANCING.MIN_LOAN, Math.min(FINANCING.MAX_LOAN, Number(v) || 0));
const clampMonths = (v) => Math.max(FINANCING.MIN_DURATION, Math.min(FINANCING.MAX_DURATION, Math.round(Number(v) || FINANCING.DEFAULT_DURATION)));

/** Standard amortization monthly payment. */
export const monthlyPayment = (loan, months, apr) => {
  const L = clampLoan(loan);
  const n = clampMonths(months);
  const r = Math.max(0, Number(apr) || 0) / 12;
  if (r === 0) return L / n;
  return (L * r) / (1 - Math.pow(1 + r, -n));
};

/**
 * Dual simulation (min / max monthly payment).
 * @returns {{ loan, months, monthlyMin, monthlyMax, totalMin, totalMax, aprMin, aprMax }}
 */
export const simulateLoan = (loanAmount, months) => {
  const loan = clampLoan(loanAmount);
  const n = clampMonths(months);
  const mMin = monthlyPayment(loan, n, APR_MIN);
  const mMax = monthlyPayment(loan, n, APR_MAX);
  return {
    loan,
    months: n,
    monthlyMin: mMin,
    monthlyMax: mMax,
    totalMin: mMin * n,
    totalMax: mMax * n,
    aprMin: APR_MIN,
    aprMax: APR_MAX,
  };
};

/**
 * Quick "from €X/month" teaser for listing cards.
 * Uses best-case APR at default term.
 * Returns { monthly } for backwards compatibility.
 */
export const estimateMonthly = (price /* , year */) => {
  const loan = clampLoan(price);
  return { monthly: monthlyPayment(loan, FINANCING.DEFAULT_DURATION, APR_MIN) };
};
